import React from 'react';
import PropTypes from 'prop-types';
import './StatCard.scss';

/**
 * Design System StatCard Component
 *
 * A card component for displaying statistics with icon, value, label, and optional trend.
 * Perfect for dashboard-style layouts showing metrics.
 *
 * @example
 * <StatCard
 *   icon="briefcase"
 *   value={15}
 *   label="Projects Completed"
 *   trend="+3"
 *   trendDirection="up"
 * />
 */
const StatCard = ({
    icon,
    value,
    label,
    trend,
    trendDirection,
    iconColor = '#FED230',
    variant = 'default',
    size = 'medium',
    onClick,
    className = '',
    style = {},
    loading = false,
    suffix,
    prefix,
    ...props
}) => {
    const baseClassName = 'ds-stat-card';
    const variantClassName = `${baseClassName}--${variant}`;
    const sizeClassName = `${baseClassName}--${size}`;
    const clickableClassName = onClick ? `${baseClassName}--clickable` : '';
    const loadingClassName = loading ? `${baseClassName}--loading` : '';

    const combinedClassName = [
        baseClassName,
        variantClassName,
        sizeClassName,
        clickableClassName,
        loadingClassName,
        className
    ].filter(Boolean).join(' ');

    const handleClick = (e) => {
        if (onClick && !loading) {
            onClick(e);
        }
    };

    const iconElement = typeof icon === 'string' ? (
        <i className={`fa fa-${icon}`} style={{ color: iconColor }} />
    ) : (
        icon
    );

    return (
        <div
            className={combinedClassName}
            style={style}
            onClick={handleClick}
            {...props}
        >
            {loading ? (
                <div className="ds-stat-card__loading">
                    <i className="fa fa-spinner fa-spin" />
                </div>
            ) : (
                <>
                    {icon && (
                        <div className="ds-stat-card__icon">
                            {iconElement}
                        </div>
                    )}

                    <div className="ds-stat-card__content">
                        <div className="ds-stat-card__value">
                            {prefix && <span className="ds-stat-card__prefix">{prefix}</span>}
                            {value !== undefined ? value : '-'}
                            {suffix && <span className="ds-stat-card__suffix">{suffix}</span>}
                        </div>

                        {label && (
                            <div className="ds-stat-card__label">
                                {label}
                            </div>
                        )}

                        {trend && (
                            <div
                                className={`ds-stat-card__trend ds-stat-card__trend--${trendDirection || 'neutral'}`}
                            >
                                {trendDirection === 'up' && <i className="fa fa-arrow-up" />}
                                {trendDirection === 'down' && <i className="fa fa-arrow-down" />}
                                <span>{trend}</span>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

StatCard.propTypes = {
    /** Icon - can be Font Awesome name (string) or React element */
    icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

    /** The statistic value to display */
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

    /** Label describing the statistic */
    label: PropTypes.string,

    /** Trend indicator (e.g., '+3', '-5', '12%') */
    trend: PropTypes.string,

    /** Direction of the trend */
    trendDirection: PropTypes.oneOf(['up', 'down', 'neutral']),

    /** Icon color */
    iconColor: PropTypes.string,

    /** Card variant style */
    variant: PropTypes.oneOf(['default', 'bordered', 'minimal', 'highlighted']),

    /** Card size */
    size: PropTypes.oneOf(['small', 'medium', 'large']),

    /** Click handler */
    onClick: PropTypes.func,

    /** Additional CSS classes */
    className: PropTypes.string,

    /** Inline styles */
    style: PropTypes.object,

    /** Loading state */
    loading: PropTypes.bool,

    /** Suffix text (e.g., 'hrs', '%') */
    suffix: PropTypes.string,

    /** Prefix text (e.g., '$', '#') */
    prefix: PropTypes.string
};

export default StatCard;
